import { useRouter } from 'next/router';
import { FC, Fragment, useState } from 'react';
import { useRecoilValue } from 'recoil';
import Button from '~/components/Button';
import Container from '~/components/Container';
import Divider from '~/components/Divider';
import CartItem from '~/components/Items/CartItem';
import LoadingPage from '~/components/LoadingPage';
import PaymentStripe from '~/components/PaymentStripe';
import ProgressDiscount from '~/components/ProgressDiscount';
import Text from '~/components/Text';
import Colors from '~/constants/colors';
import CheckoutDrawer from '~/sections/CheckoutDrawer';
import CheckoutSummary from '~/sections/CheckoutSummary';
import { CartAtom } from '~/services/cart';
import ReMaketing, { REMARKETING_CART } from '~/services/tracking/ReMaketing';
import styles from '~/styles/pages/Cart.module.scss';

const Cart: FC = () => {
    const router = useRouter();
    const [selectedCartItems, setSelectedCartItems] = useState<string[]>([]);
    const [errorPaymentStripe, setErrorPaymentStripe] = useState<any>();
    const cartSelector = useRecoilValue(CartAtom.cartSelector);

    const handleProceedToCheckout = () => {
        router.push('/checkout');
    };

    const handleCheckedByCartItem = (productSku: string) => {
        const isExist = selectedCartItems.includes(productSku);

        if (isExist) {
            setSelectedCartItems(selectedCartItems.filter((item) => item !== productSku));
        } else {
            setSelectedCartItems([...selectedCartItems, productSku]);
        }
    };
    const handleErrorPaymentStripe = (error: any) => {
        setErrorPaymentStripe(error);
    };

    return (
        <Fragment>
            <ReMaketing data={{ cart: cartSelector }} gtagType={REMARKETING_CART} />
            <Container rootClassName={styles.cart} className={styles.cart_wrapper}>
                {cartSelector.loading && <LoadingPage text="Loading cart..." />}
                {!errorPaymentStripe && (
                    <div className="bg-white rounded-xl overflow-hidden p-2 lg:hidden">
                        <PaymentStripe onError={handleErrorPaymentStripe} />
                    </div>
                )}
                <section className={styles.my_cart}>
                    <Text type="title-3" color={Colors.GREY_900} className={styles.title}>
                        My Cart
                    </Text>

                    <div className={styles.cart_items}>
                        <div className="px-4  w-full flex justify-center">
                            <ProgressDiscount />
                        </div>
                        <div className={styles.header}>
                            <Text type="label-4" color={Colors.GREY_700} className={styles.header_title}>
                                Product
                            </Text>
                            <Text type="label-4" color={Colors.GREY_700} className={styles.header_title}>
                                Price
                            </Text>
                            <Text type="label-4" color={Colors.GREY_700} className={styles.header_title}>
                                Quantity
                            </Text>
                            <Text type="label-4" color={Colors.GREY_700} className={styles.header_title}>
                                Total
                            </Text>
                            <Text type="label-4" color={Colors.GREY_700} className={styles.header_title}>
                                Action
                            </Text>
                        </div>
                        <Divider dividerColor={Colors.GREY_50} />
                        {cartSelector.loading &&
                            !cartSelector.items?.length &&
                            Array.from({ length: 2 }, (index: number) => (
                                <CartItem key={index} cartItem={{} as any} loading={true} />
                            ))}
                        {!cartSelector.loading &&
                            !!cartSelector?.items?.length &&
                            cartSelector.items?.map((cartItem) => (
                                <CartItem cartItem={cartItem} key={cartItem.id} />
                            ))}{' '}
                        {!cartSelector.loading && !cartSelector?.items?.length && (
                            <div className="flex justify-center items-center p-8">
                                <Text type="title-3">Your cart is empty</Text>
                            </div>
                        )}
                    </div>
                </section>
                <section className={styles.cart_total}>
                    <Text type="title-3" color={Colors.GREY_900} className={styles.title}>
                        Cart Total
                    </Text>

                    <CheckoutSummary showShipping={false} />

                    <div className=" flex-col gap-3 px-4 lg:px-8 hidden lg:flex">
                        <Button disabled={!cartSelector.items?.length} onClick={handleProceedToCheckout}>
                            Proceed to checkout
                        </Button>

                        <PaymentStripe />
                    </div>
                </section>
            </Container>
            <CheckoutDrawer
                isPlaceholder={true}
                okText="Proceed to checkout"
                okProps={{
                    onClick: handleProceedToCheckout
                }}
            />
        </Fragment>
    );
};

export default Cart;
